import { useEffect, useState, type ReactNode } from 'react'
import { ConfigProvider, theme as antdTheme } from 'antd'
import zhCN from 'antd/locale/zh_CN'

// antd 的主题桥。
//
// 项目里 24 个文件直接用 antd 组件（Card / List / Input / Select / Pagination…），
// 而此前**一个 ConfigProvider 都没有** —— 它们一直跑在 antd 的默认浅色主题上。
// 浅色时看不出问题，加了暗色就暴露了：整页是深的，中间杵着一块白色的 antd Card。
//
// 这里只做两件事，都在一个地方做完，不去逐个组件覆盖样式：
//
// 1. **跟随系统主题切 algorithm。** antd v5 的 darkAlgorithm 会重算它自己那整套
//    设计变量，比我们在外面用 CSS 覆盖它的类名可靠得多 —— 覆盖类名要跟着
//    antd 的内部结构走，它升个小版本就可能全断。
//
// 2. **把它的几个关键色对到我们的 token 上。** 读的是 CSS 变量的计算值而不是
//    写死一份 hex：配色改了（比如换暗色方案）这里自动跟上，不用记得来改第二处。
function readToken(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

function useIsDark(): boolean {
  // 先给一个与服务端一致的初值，挂载后再按真实媒体查询纠正 ——
  // 直接在初值里读 matchMedia 会让 SSR 与首帧不一致。
  const [dark, setDark] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => {
      // data-theme 是显式选择，优先于系统设置（与 application.css 里的规则同构）
      const explicit = document.documentElement.getAttribute('data-theme')
      setDark(explicit === 'dark' || (explicit !== 'light' && mq.matches))
    }
    sync()
    mq.addEventListener('change', sync)
    // 将来加手动切换时，data-theme 的变化也要被看到
    const mo = new MutationObserver(sync)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => {
      mq.removeEventListener('change', sync)
      mo.disconnect()
    }
  }, [])

  return dark
}

export default function AntdTheme({ children }: { children: ReactNode }) {
  const dark = useIsDark()

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: readToken('--brand-primary-strong', '#D03C40'),
          colorLink: readToken('--brand-clash', '#5C2751'),
          colorBgBase: readToken('--brand-bg', '#FDF6F5'),
          colorTextBase: readToken('--brand-ink', '#32162B'),
          colorBorder: readToken('--brand-line', 'rgba(50,22,43,.11)'),
          // 输入框 / 下拉 / 默认按钮的底。不给的话 darkAlgorithm 会从 colorBgBase
          // 自己推一档，#0A0A28 推出来是 rgb(11,11,79) —— 一块很扎眼的饱和深蓝。
          colorBgContainer: readToken('--brand-card', '#FFFFFF'),
          borderRadius: 12,
          fontFamily: readToken('--font-body', 'sans-serif'),
        },
        components: {
          // Card 的底与我们的卡片面同值，否则暗色下它比周围亮一块
          Card: { colorBgContainer: readToken('--brand-card', '#FFFFFF') },
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}
